// Field Offline Resource Registry
//
// Single source of truth for what gets pre-cached when a sync completes.
// Sync controllers call warmUrlsForSync(responses) and pass the result to
// the service worker via WARM_CACHE postMessage.
//
// There are two complementary systems for offline support:
//
//   1. Shell pages (Rails-side, config/initializers/field_offline_manifest.rb)
//      Declares which paths the SW pre-caches and serves as shells when the
//      network is unavailable. A shell with `covers` acts as a prefix match --
//      the SW serves it for any sub-path the Tasker navigates to offline.
//
//   2. Resource warming (this file, JS-side)
//      Declares what individual URLs must be pre-cached per synced record so
//      that navigating to them while offline hits SW cache rather than the
//      network. Sync controllers call warmUrlsForSync() after each download.
//
// When extracted to a Gem/JS library:
//   - SHELL_PAGES merges into the Rails manifest (auto-generated from
//     controller declarations, eliminating the current duplication)
//   - Each RESOURCES entry becomes a concern annotation on the Rails model
//     or API serialiser, e.g. offline_resource warms: :complete_url
//   - warmUrlsForSync becomes a shared utility imported automatically by
//     any sync controller that includes the library concern
//
// Adding a new offline resource
// ─────────────────────────────
// 1. Add an entry to RESOURCES below
// 2. Declare the shell page in config/initializers/field_offline_manifest.rb
//    (include `covers:` if it acts as a prefix shell for sub-paths)
// 3. Add offline_capable to the Rails controller
// 4. Add a Stimulus controller to handle offline rendering if strategy is :indexeddb
// 5. Add the IndexedDB store to field_db.js if the resource has its own store
// See docs/dev_patterns/offline-resources.md for the full checklist.

import { currentLevel, LEVEL_DEGRADED } from "./field_network_health"

// ── SurveyJS CDN assets ───────────────────────────────────────────────────────
//
// Core rendering assets needed for survey pages in the field app.
// Warmed on every sync so survey pages work offline even on first visit.
// Creator, analytics, and tabulator are admin-only -- do NOT add them here.
const SURVEY_CDN_ASSETS = [
  'https://cdn.jsdelivr.net/npm/survey-core@2.2.0/survey-core.min.css',
  'https://cdn.jsdelivr.net/npm/survey-core@2.2.0/survey.core.min.js',
  'https://cdn.jsdelivr.net/npm/survey-js-ui@2.2.0/survey-js-ui.min.js',
]

// ── Shell pages ──────────────────────────────────────────────────────────────
//
// These are always included in the warming payload regardless of sync content.
// They match the `path` entries in config/initializers/field_offline_manifest.rb.
// Keep the two in sync when adding new shell pages.

export const SHELL_PAGES = [
  "/field",
  "/field/today",
  "/field/missions",    // covers /field/missions/:id via SW prefix match
  "/field/playbooks",   // covers /field/playbooks/:id and nested entry pages via SW prefix match (card #4461)
  "/field/places",      // places index (standalone cached snapshot)
  "/field/offline-app", // IndexedDB shell -- covers /field/places/:id and /field/responses/:id via SW prefix match (Card 2899)
  "/field/activity",
  "/field/sync",
  "/field/more",
  "/field/photos",      // My Photos gallery -- local at-risk blobs render offline; server union warms when online (W3)
  "/field/diagnostics",
  "/field/install",      // Install Status -- secondary install group detail (card #4916)
  "/field/cards",     // Raise a Ticket -- queued tickets render from Dexie; must open when the app itself is broken (#2617)
  "/field/offline",     // generic offline fallback -- warmed into PAGE_CACHE as backup if SHELL_CACHE install fails
]

// ── Resource declarations ─────────────────────────────────────────────────────
//
// Each entry declares:
//   name      -- identifier matching the server resource type
//   stores    -- IndexedDB store(s) this resource populates (informational)
//   warmUrls  -- function(serverRecord) → string[] of URLs to pre-cache
//
// The warmUrls function receives the raw server JSON record from the prefetch
// API response. Return an empty array if the resource relies entirely on a
// shell page (no individual URLs need warming).

export const RESOURCES = {
  // warm_urls is generated server-side by the prefetch controller so that
  // Rails owns all URL generation. To add a new cacheable URL for a resource,
  // update warm_urls_for_* in:
  //   app/controllers/api/v1/field/prefetch_controller.rb
  mission: {
    name: "mission",
    stores: [],  // no IndexedDB store -- pages cached as HTML snapshots
    warmUrls: (m) => m.warm_urls || []
  },

  place: {
    name: "place",
    stores: ["cached_places"],
    // Bad connectivity places get their show URL warmed so the SW can serve a real cached
    // HTML page on exact match. Other places still fall through to the /field/offline-app shell.
    warmUrls: (p) => p.warm_urls || []
  },

  response: {
    name: "response",
    stores: ["cached_responses"],
    warmUrls: (r) => r.warm_urls || []
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

// Returns the deduplicated asset URLs from the prefetch API response.
// These are cross-origin binary assets (logos, PDF flyers) that need caching
// via the CACHE_ASSETS message to the service worker.
// Deduplication happens server-side; this just extracts the list.
export function extractMissionAssetUrls(prefetchData) {
  return prefetchData?.mission_assets?.urls || []
}

// Card #4117 -- the order a Tasker needs their missions in: started first,
// then anything scheduled for today (soonest first), then poor signal places
// (hardest to fetch later), then the rest by schedule, unscheduled last.
// A stable sort, so equal responses keep the order the server sent.
export function prioritiseResponses(responses, now = new Date()) {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const endOfToday   = startOfToday + 24 * 60 * 60 * 1000

  const rank = (r) => {
    if (r.status === "started") return 0
    const at = r.schedule_at ? new Date(r.schedule_at).getTime() : null
    if (at !== null && at >= startOfToday && at < endOfToday) return 1
    if (r.place?.bad_connectivity) return 2
    return at !== null ? 3 : 4
  }
  const when = (r) => (r.schedule_at ? new Date(r.schedule_at).getTime() : Infinity)

  return responses
    .map((r, index) => ({ r, index, rank: rank(r) }))
    .sort((a, b) => a.rank - b.rank || (a.rank === 1 || a.rank === 3 ? when(a.r) - when(b.r) : 0) || a.index - b.index)
    .map(entry => entry.r)
}

// Returns the deduplicated list of URLs to warm given the prefetch payload,
// most important first (card #4117): the app shell and the survey scripts, then
// for each response in priority order its own pages, its mission page and its
// place page the first time that mission or place appears. Pass the result to
// warmInBatches (field_warm_queue.js), which sends it in small batches.
export function warmUrlsForSync(responses) {
  const seenMissionIds = new Set()
  const seenPlaceIds = new Set()
  const perResponse = []

  for (const r of prioritiseResponses(responses)) {
    perResponse.push(...RESOURCES.response.warmUrls(r))

    if (r.mission?.id && !seenMissionIds.has(r.mission.id)) {
      seenMissionIds.add(r.mission.id)
      perResponse.push(...RESOURCES.mission.warmUrls(r.mission))
    }

    // Bad connectivity places get their show URL warmed; other places still
    // rely on the /field/offline-app shell prefix match.
    if (r.place?.id && !seenPlaceIds.has(r.place.id)) {
      seenPlaceIds.add(r.place.id)
      perResponse.push(...RESOURCES.place.warmUrls(r.place))
    }
  }

  return [...new Set([...SHELL_PAGES, ...SURVEY_CDN_ASSETS, ...perResponse])]
}

// ── Turbo-visited pages (card #3827) ─────────────────────────────────────────
//
// A Turbo Drive visit is a fetch(), not a navigation, so the service worker's
// NetworkFirst navigate route never caches it. A page the tasker just looked
// at online -- a response they started, the place they're standing in -- then
// isn't in PAGE_CACHE, and offline /field/responses/:id falls through to the
// offline-app shell. warmVisitedPage asks the SW to cache the page just
// visited, for these pages only (exact pathnames, no query strings, not
// /preview).
const WARMABLE_VISITED_PATHS = [
  /^\/field\/today$/,
  /^\/field\/places\/\d+$/,
  /^\/field\/responses\/\d+$/
]

// Once per URL per 30s -- Turbo can render the same page twice (preview, then
// fresh), and there's no value in re-fetching a page just cached.
const VISITED_WARM_DEBOUNCE_MS = 30_000
const lastVisitedWarmAt = new Map()

export function isWarmableVisitedPath(pathname) {
  return WARMABLE_VISITED_PATHS.some(pattern => pattern.test(pathname))
}

// Posts { type: "WARM_CACHE", urls: [pathname] } to the SW when the page is
// warmable, the app is online and not Degraded (don't spend a struggling
// connection re-fetching a page), and this URL wasn't warmed in the last 30s.
// Fire-and-forget: never waits on the SW. Returns true when a warm was sent.
export function warmVisitedPage(pathname, { now = Date.now() } = {}) {
  if (!isWarmableVisitedPath(pathname)) return false
  if (localStorage.getItem("field:online") === "false") return false
  if (currentLevel() <= LEVEL_DEGRADED) return false
  const sw = navigator.serviceWorker?.controller
  if (!sw) return false
  if (now - (lastVisitedWarmAt.get(pathname) || 0) < VISITED_WARM_DEBOUNCE_MS) return false

  lastVisitedWarmAt.set(pathname, now)
  sw.postMessage({ type: "WARM_CACHE", urls: [pathname] })
  return true
}
